/**
 * @file Adaptateurs du serveur de jeu aux contrats du client : sauvegarde cloud (SyncAdapter) et hub social temps reel (HubClient + extras herbe a chat).
 * Adapte de GameServer/src/sdk/catgunnerAdapters.ts (memes messages `room.msg`).
 */
import { GameApiError } from './GameClient';
import type { GameClient, RealtimeSocket } from './GameClient';
import type { ChatMessage, HubFx, HubPlayerState, PublicCatLook } from './protocol';
import type { HubClientEvents } from './HubClient';
import type { RemoteSave, SyncAdapter } from './SyncAdapter';

/** Sauvegarde cloud : la revision « serveur » est celle du serveur (incrementee par push) ; un conflit renvoie `accepted: false` + la revision serveur. */
export class ServerSyncAdapter implements SyncAdapter {
  readonly enabled = true;
  private serverRevision = 0;

  constructor(private readonly client: GameClient) {}

  get revision(): number {
    return this.serverRevision;
  }

  async pull(): Promise<RemoteSave | null> {
    const r = await this.client.pullSave();
    this.serverRevision = r.revision;
    return r.found && r.payload ? { revision: r.revision, savedAtMs: r.savedAtMs ?? 0, payload: r.payload } : null;
  }

  async push(_playerId: string, save: RemoteSave): Promise<{ accepted: boolean; serverRevision: number }> {
    try {
      const r = await this.client.pushSave({ baseRevision: this.serverRevision, payload: save.payload, savedAtMs: save.savedAtMs });
      this.serverRevision = r.revision;
      return { accepted: true, serverRevision: r.revision };
    } catch (e) {
      if (e instanceof GameApiError && e.code === 'save_conflict') {
        const server = (e.details as { revision?: number } | undefined)?.revision ?? this.serverRevision;
        return { accepted: false, serverRevision: server };
      }
      throw e;
    }
  }

  /** Apres arbitrage « garder la version locale » : ecrase la version serveur. */
  async forcePush(save: RemoteSave): Promise<number> {
    const r = await this.client.pushSave({ baseRevision: this.serverRevision, payload: save.payload, savedAtMs: save.savedAtMs, force: true });
    this.serverRevision = r.revision;
    return r.revision;
  }
}

export type CatnipEvent =
  | { kind: 'spawn'; id: string; x: number; y: number }
  | { kind: 'taken'; id: string; by: string }
  | { kind: 'claimed'; id: string; total: number; /** Herbes gagnees (2 pendant le week-end double). */ gain: number }
  | { kind: 'missed'; id: string };

export interface HubExtras {
  onCatnip?(ev: CatnipEvent): void;
  /** Etat initial : instance, saison du decor, herbe deja presente. */
  onWelcome?(w: { instanceId: string; name: string; season: string; catnip: Array<{ id: string; x: number; y: number }>; self: HubPlayerState }): void;
  /** Le serveur a corrige notre position (vitesse ou limites de la carte). */
  onCorrection?(pos: { x: number; y: number }): void;
  onJoined?(player: HubPlayerState): void;
  onLook?(playerId: string, look: PublicCatLook): void;
  onEmote?(playerId: string, emote: string): void;
  /** Effet ponctuel d'un autre joueur (non verifie : voir hubLook.sanitizeFx). */
  onFx?(playerId: string, fx: unknown): void;
}

/** Hub social : rejoint (ou cree) une instance, envoie les positions, recoit les instantanes. */
export class ServerHubClient {
  connected = false;
  roomId: string | null = null;
  private sock: RealtimeSocket | null = null;
  private off: Array<() => void> = [];

  constructor(
    private readonly client: GameClient,
    private readonly extras: HubExtras = {},
  ) {}

  async connect(_playerId: string, look: PublicCatLook, events: HubClientEvents, o: { roomId?: string; friendId?: string; private?: boolean } = {}): Promise<void> {
    const sock = await this.client.connect();
    this.sock = sock;
    sock.closed((_c, reason) => {
      this.connected = false;
      events.onDisconnected(reason || 'closed');
    });
    this.off.push(
      sock.onRoom((d) => {
        switch (d['t']) {
          case 'welcome':
            this.extras.onWelcome?.(d as never);
            break;
          case 'snapshot':
            events.onSnapshot(d['players'] as HubPlayerState[], d['serverTimeMs'] as number);
            break;
          case 'joined':
            this.extras.onJoined?.(d['player'] as HubPlayerState);
            break;
          case 'look':
            this.extras.onLook?.(String(d['playerId']), d['look'] as PublicCatLook);
            break;
          case 'fx':
            this.extras.onFx?.(String(d['playerId']), d['fx']);
            break;
          case 'emote':
            this.extras.onEmote?.(String(d['playerId']), String(d['emote']));
            break;
          case 'left':
            events.onPlayerLeft(String(d['playerId']));
            break;
          case 'chat':
            events.onChat(d['message'] as ChatMessage);
            break;
          case 'correct':
            this.extras.onCorrection?.({ x: d['x'] as number, y: d['y'] as number });
            break;
          case 'catnip':
            this.extras.onCatnip?.({ kind: 'spawn', id: String(d['id']), x: d['x'] as number, y: d['y'] as number });
            break;
          case 'catnipTaken':
            this.extras.onCatnip?.({ kind: 'taken', id: String(d['id']), by: String(d['by']) });
            break;
          case 'catnipClaimed':
            this.extras.onCatnip?.({ kind: 'claimed', id: String(d['id']), total: d['total'] as number, gain: typeof d['gain'] === 'number' ? (d['gain'] as number) : 1 });
            break;
          case 'catnipMissed':
            this.extras.onCatnip?.({ kind: 'missed', id: String(d['id']) });
            break;
        }
      }),
    );
    const room = await sock.joinRoom<{ id: string }>('hub', o);
    this.roomId = room.id;
    this.connected = true;
    sock.roomSend(room.id, { t: 'hello', look: { ...look } });
  }

  disconnect(): void {
    for (const f of this.off) f();
    this.off = [];
    this.sock?.close();
    this.sock = null;
    this.roomId = null;
    this.connected = false;
  }

  /** Position + visee de l'arme et etat range / sorti (les serveurs plus anciens ignorent `aim` / `stowed`). */
  sendMove(x: number, y: number, rot: number, moving: boolean, aim = rot, stowed = true): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'move', x, y, rot, moving, aim, stowed });
  }
  /** Nouvelle apparence (arme, skin, aura, feu d'artifice...). */
  sendLook(look: PublicCatLook): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'look', look: { ...look } });
  }
  /** Effet ponctuel visible par les autres joueurs du salon. */
  sendFx(fx: HubFx): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'fx', fx: { ...fx } });
  }
  sendEmote(emote: string): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'emote', emote });
  }
  sendChat(_channel: 'hub' | 'guild' | 'private', payload: { text?: string; quickEmoteId?: string }): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'chat', ...payload });
  }
  sendBlock(playerId: string): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'block', playerId });
  }
  sendReport(playerId: string, reason: string): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'report', playerId, reason });
  }
  /** Reclame une brindille d'herbe a chat (le serveur verifie la distance et l'ordre d'arrivee). */
  claimCatnip(id: string): void {
    if (this.roomId) this.sock?.roomSend(this.roomId, { t: 'catnipClaim', id });
  }
}
